<div class="w-full px-4 py-6 md:px-6 lg:px-20">
  @if (isLoading()) {
    <div class="space-y-6">
      <div class="h-10 w-72 animate-pulse rounded bg-slate-100"></div>
      <div class="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div
          class="h-64 animate-pulse rounded-2xl border border-slate-200 bg-slate-50"
        ></div>
        <div class="space-y-4">
          <div class="h-10 w-40 animate-pulse rounded bg-slate-100"></div>
          @for (item of [1, 2, 3, 4]; track item) {
            <div
              class="h-28 animate-pulse rounded-2xl border border-slate-200 bg-slate-50"
            ></div>
          }
        </div>
      </div>
    </div>
  } @else {
    <div class="space-y-6">
      <h1 class="text-[32px] font-semibold tracking-tight leading-[150%] text-gray">
        {{ sellerName() }} {{ 'sellerReviewsPage.titleSuffix' | translate }}
      </h1>

      <div class="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside class="rounded-lg border h-fit border-[#E6E6E6] p-4">
          <div class="flex items-end gap-2">
            <span class="text-[48px] font-bold leading-none text-[#191C1D]">
              {{ averageRating() | number: '1.1-1' }}
            </span>
            <span class="mb-1 leading-6 font-medium text-[#3E484D]">/ 5.0</span>
          </div>

          <div class="mt-6 space-y-4">
            @for (item of ratingBreakdown(); track item.stars) {
              <div
                class="grid grid-cols-[20px_minmax(0,1fr)_26px] items-center gap-4"
              >
                <span class="text-sm font-medium leading-5 text-[#191C1D]">{{
                  item.stars
                }}</span>
                <div class="h-1.5 overflow-hidden rounded-full bg-[#E9EDF1]">
                  <div
                    class="h-full rounded-full bg-[#2C93BE]"
                    [style.width.%]="item.percentage"
                  ></div>
                </div>
                <span class="text-xs text-[#3E484D]">{{ item.percentage }}%</span>
              </div>
            }
          </div>

          <p class="mt-8 text-sm leading-5 italic text-[#3E484D]">
            {{
              'sellerReviewsPage.verifiedPurchases'
                | translate: { count: totalReviews() }
            }}
          </p>
        </aside>

        <section class="min-w-0">
          <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <h2 class="text-[20px] font-semibold text-[#202020]">
              {{ 'reviewsPage.communityFeedback' | translate }}
            </h2>

            <div class="relative self-start sm:self-auto">
              <button
                type="button"
                (click)="showFilterDropdown.set(!showFilterDropdown())"
                class="rounded-xl border border-[#E6E6E6] bg-white-light px-5 py-3 text-sm font-medium text-[#202020] transition-colors hover:bg-slate-50"
              >
                {{ 'reviewsPage.filterByStars' | translate }}
              </button>

              @if (showFilterDropdown()) {
                <div
                  class="absolute end-0 top-full z-20 mt-2 min-w-44 rounded-xl border border-[#E6E6E6] bg-white-light p-2 shadow-lg"
                >
                  <button
                    type="button"
                    (click)="filterByRating(null)"
                    class="block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50"
                    [class.bg-sky-50]="selectedFilter() === null"
                    [class.text-[#2596BE]]="selectedFilter() === null"
                  >
                    {{ 'reviewsPage.allRatings' | translate }}
                  </button>

                  @for (rating of [5, 4, 3, 2, 1]; track rating) {
                    <button
                      type="button"
                      (click)="filterByRating(rating)"
                      class="block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50"
                      [class.bg-sky-50]="selectedFilter() === rating"
                      [class.text-[#2596BE]]="selectedFilter() === rating"
                    >
                      {{ rating }} {{ 'reviewsPage.stars' | translate }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>

          @if (totalReviews() === 0) {
            <div class="py-12 text-center text-[#6B7280]">
              {{ 'reviewsPage.noReviews' | translate }}
            </div>
          } @else {
            <div class="mt-8 space-y-8 divide-y divide-[#EAEAEA]">
              @for (review of pagedReviews(); track trackByReview($index, review)) {
                <article class="pb-4 first:pt-0">
                  <div class="flex gap-3">
                    @if (review.avatarUrl) {
                      <img
                        [src]="review.avatarUrl"
                        [alt]="review.customerDisplayName"
                        class="size-11 shrink-0 rounded-full object-cover"
                      />
                    } @else {
                      <div
                        class="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#E8F4FA] text-sm font-semibold text-[#2C93BE]"
                      >
                        {{ getInitials(review.customerDisplayName) }}
                      </div>
                    }

                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="font-medium leading-[150%] text-gray text-sm">
                          {{ review.customerDisplayName }}
                        </span>
                        <span class="text-gray">•</span>
                        <span class="text-white-darkest">
                          {{ formatDate(review.createdAtUtc) }}
                        </span>
                      </div>

                      <div class="mt-2 flex items-center gap-2">
                        <span class="text-sm font-semibold text-gray">
                          {{ review.rating | number: '1.1-1' }}
                        </span>
                        <div class="flex gap-1">
                          @for (star of stars; track star) {
                            <span
                              class="flex h-4 w-4 **:stroke-current"
                              [class.text-[#FFC107]]="
                                isStarFilled(review.rating, star)
                              "
                              [class.text-[#D9DDE3]]="
                                !isStarFilled(review.rating, star)
                              "
                            >
                              <sham-i-RatingStar />
                            </span>
                          }
                        </div>
                      </div>

                      <p
                        class="mt-3 max-w-[860px], text-sm leading-7 text-white-darkest"
                      >
                        {{ review.text }}
                      </p>
                    </div>
                  </div>
                </article>
              }
            </div>
          }

          @if (totalPages() > 1) {
            <div class="mt-8 flex items-center justify-end gap-4">
              <div class="relative">
                <button
                  type="button"
                  (click)="showPageDropdown.set(!showPageDropdown())"
                  class="flex h-8 items-center gap-2 rounded-full bg-[#F3F4F6] px-3 py-1 text-xs text-white-darkest"
                >
                  {{ currentPage() }}
                  <sham-i-fi_chevron-down />
                </button>

                @if (showPageDropdown()) {
                  <div
                    class="absolute end-0 top-full z-20 mt-2 min-w-20 rounded-xl border border-[#E6E6E6] bg-white p-1 shadow-lg"
                  >
                    @for (page of pageNumbers(); track page) {
                      <button
                        type="button"
                        (click)="goToPage(page)"
                        class="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50"
                        [class.font-semibold]="page === currentPage()"
                      >
                        {{ page }}
                      </button>
                    }
                  </div>
                }
              </div>

              <span class="text-sm text-[#666666]">
                {{ 'reviewsPage.of' | translate }} {{ totalPages() }}
                {{ 'reviewsPage.pages' | translate }}
              </span>

              <div class="flex items-center gap-3 text-white-darkest">
                <button
                  type="button"
                  (click)="goToPreviousPage()"
                  [disabled]="currentPage() === 1"
                  class="disabled:cursor-not-allowed disabled:opacity-40 rotate-90"
                >
                  <sham-i-arrow-down-01-Stroke />
                </button>
                <button
                  type="button"
                  (click)="goToNextPage()"
                  [disabled]="currentPage() === totalPages()"
                  class="disabled:cursor-not-allowed disabled:opacity-40 -rotate-90"
                >
                  <sham-i-arrow-down-01-Stroke />
                </button>
              </div>
            </div>
          }
        </section>
      </div>
    </div>
  }
</div>
